import type { ReactNode } from "react";
import { Fingerprint } from "lucide-react";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";

interface PasskeyPromptProps {
  onConfirm: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  error?: string | null;
  title?: string;
  description?: string;
  confirmLabel?: string;
  /** A second way out — "Use an authenticator code instead". */
  alternate?: ReactNode;
}

/**
 * The passkey half of the second-factor challenge, opposite `MfaForm`.
 *
 * There is nothing to type here, so the screen is one button: the ceremony
 * lives in the browser's own system prompt. It is still explicit rather than
 * fired on mount — an unrequested Touch ID sheet over a page the user has not
 * finished reading is startling, and a prompt that appears once cannot be
 * summoned back if it is dismissed.
 */
export function PasskeyPrompt({
  onConfirm,
  onCancel,
  isLoading = false,
  error,
  title = "Two-Factor Authentication",
  description = "Confirm it's you with the passkey on this device",
  confirmLabel = "Use passkey",
  alternate,
}: PasskeyPromptProps) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-accent-500/10">
          <Fingerprint aria-hidden="true" className="size-6 text-accent-500" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          {title}
        </h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {description}
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-danger-subtle p-3 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
        )}
        <Button
          type="button"
          className="flex-1"
          onClick={onConfirm}
          disabled={isLoading}
        >
          {isLoading ? (
            <Spinner size="sm" />
          ) : (
            <Fingerprint aria-hidden="true" className="size-4" />
          )}
          {confirmLabel}
        </Button>
      </div>

      {alternate ? <div className="text-center">{alternate}</div> : null}
    </div>
  );
}
